import React, { useState } from 'react';

const content = {
  en: {
    toggleLabel: '中文',
    title: 'Terms of Service',
    effectiveDate: 'Effective Date: May 4, 2026',
    serviceName: 'Service Name: Vessel (the "Service," "we," "us," or "our")',
    introText:
      'By installing or using the Service, you agree to be bound by these Terms of Service (the "Terms"). If you do not agree to these Terms, do not install or use the Service.',
    disclaimer:
      'Vessel is a personal mood-journaling application built for self-reflection and personal growth — a quiet space to get to know yourself a little better. Everyone experiences highs and lows, and recording how you feel does not mean you are unwell or have a mental-health condition. If you are experiencing emotional or psychological difficulties, please consult a licensed mental-health professional. Vessel is not a medical device and is not a substitute for professional medical advice, diagnosis, or treatment.',
    s1Title: '1. Definitions',
    defs: [
      ['You / User:', 'Any person who installs or uses the Service.'],
      [
        'Service:',
        'The Vessel iOS application, including all of its features and updates.',
      ],
      [
        'Content:',
        'Any text, drawing, audio, video, score, note, or other entry you create within the Service.',
      ],
    ],
    s2Title: '2. Eligibility & Compliance',
    s2Body:
      'You represent and warrant that you: (a) have the legal capacity to use the Service; (b) will comply with all applicable laws; and (c) will not use the Service in any unlawful or prohibited manner.',
    s3Title: '3. Nature of the Service',
    s3Body1:
      'Vessel is a personal mood-journaling iOS application designed to support self-reflection and personal growth. You can record short mood scores, write notes, and create private entries (text, sketches, audio, or video) inside the app.',
    s3Body2Bold:
      'Vessel is not a medical device, and is not a substitute for professional medical, psychological, or psychiatric advice, diagnosis, or treatment.',
    s3Body2: ' See Section 10 (Mental-Health Disclaimer) for details.',
    s4Title: '4. Local-Only Storage; No Account; Backup',
    s4Bullets: [
      [
        'Local Storage.',
        " All Content you create in Vessel is stored locally on your device, inside iOS's standard application sandbox. We do not operate any server, cloud synchronization, or backup service for the Service.",
      ],
      [
        'No Account.',
        ' The Service does not require sign-up, login, or account creation.',
      ],
      [
        'Backup Function.',
        ' The Service includes an in-app Backup button. When activated, the Service writes a JSON-formatted snapshot of your Content to local storage on your device. This backup is generated and held entirely on the local device; we do not operate any server-side backup, cloud sync, or recovery mechanism.',
      ],
      [
        'No Guarantee Against Data Loss.',
        ' The Backup function is a frontend-only convenience feature and does not guarantee against data loss. You remain solely responsible for preserving any backup file (for example, by copying it to your own iCloud Drive, computer, or another secure storage location). We bear no responsibility for any loss of Content, whether due to device loss, reset, factory restore, uninstallation, file corruption, or any other cause.',
      ],
    ],
    s5Title: '5. Your Content',
    s5Body:
      'All Content you create in Vessel belongs to you. We do not access, view, copy, transmit, or use your Content for any purpose. Because we never receive your Content, we hold no rights in it and bear no responsibility for the substance of any entry you create.',
    s6Title: '6. Acceptable Use',
    s6Body: 'You agree not to:',
    s6List: [
      'Use the Service in any manner that violates applicable law or regulation;',
      'Reverse engineer, decompile, or interfere with the Service, except where such activity is expressly permitted by law;',
      'Attempt to circumvent or compromise the security of the device on which the Service is installed; or',
      'Engage in any other conduct that disrupts or harms the integrity of the Service.',
    ],
    s7Title: '7. Intellectual Property',
    s7Body:
      'The Vessel software, name, design, trademarks, logos, and related materials are owned by us or our licensors and are protected by intellectual-property laws. The Terms grant you only the limited right to use the Service as installed on your authorized device, and do not grant you any other rights in those materials.',
    s8Title: '8. "As Is"; No Warranties',
    s8Body:
      'The Service is provided "as is" and "as available." We make no warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, accuracy, completeness, timeliness, availability, or non-infringement. We do not guarantee that the Service will be uninterrupted, error-free, or fully secure. You assume all risk for your use of the Service.',
    s9Title: '9. Limitation of Liability',
    s9Body:
      'To the maximum extent permitted by law, in no event shall we be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, profits, or goodwill, arising out of or in connection with your use of, or inability to use, the Service.',
    s10Title: '10. Mental-Health Disclaimer',
    s10Body1:
      'Vessel is intended for personal reflection and self-understanding only. The mood scoring, calendar, charts, and journaling features provided by the Service are not diagnostic tools and do not constitute medical, psychological, or psychiatric advice. Recording your moods does not mean you have a mental-health condition; everyone experiences ordinary highs and lows.',
    s10Body2:
      'If you are experiencing emotional distress, a mental-health crisis, or thoughts of self-harm, please reach out to a licensed mental-health professional, your local emergency services, or a recognized crisis hotline. Do not rely on the Service in lieu of professional help.',
    s11Title: '11. Maintenance; Changes',
    s11Body:
      'We may update, modify, or discontinue any part of the Service at any time for technical, operational, or legal reasons. We will use commercially reasonable efforts to reduce impact but do not guarantee advance notice or uninterrupted availability.',
    s12Title: '12. Privacy',
    s12Body:
      'Please review our Privacy Policy for how we handle (or, more accurately, do not handle) personal data. Your use of the Service is also subject to our Privacy Policy.',
    s13Title: '13. Termination',
    s13Body:
      'You may stop using the Service at any time by uninstalling it from your device. We may also terminate or suspend access to the Service at any time, without notice, for any conduct that we believe violates these Terms or is harmful to other users, to us, or to third parties.',
    s14Title: '14. Changes to These Terms',
    s14Body:
      'We may update these Terms periodically. Unless stated otherwise, updated Terms are effective upon posting within the Service or on our official pages. Your continued use of the Service indicates acceptance of the updates.',
    footer: 'Document effective as of May 4, 2026.',
  },
  zh: {
    toggleLabel: 'EN',
    title: '服務條款',
    effectiveDate: '生效日期：2026 年 5 月 4 日',
    serviceName: '服務名稱：Vessel（下稱「本服務」、「我們」）',
    introText:
      '當你安裝或使用本服務，即表示你同意受本服務條款（下稱「本條款」）約束。如你不同意本條款，請不要安裝或使用本服務。',
    disclaimer:
      'Vessel 是一個個人心情紀錄應用程式，旨在協助你進行自我反思與個人成長 —— 一個安靜的空間，讓你更認識自己。每個人都會有情緒的高低起伏，紀錄心情並不代表你有病或心理出問題。如你正面對情緒或精神上的困擾，請務必諮詢持牌的精神健康專業人士。Vessel 並非醫療裝置，亦不能取代任何專業醫療建議、診斷或治療。',
    s1Title: '一、定義',
    defs: [
      ['你／用戶：', '任何安裝或使用本服務的人士。'],
      ['本服務：', '指 Vessel iOS 應用程式，包括其所有功能與更新。'],
      [
        '內容：',
        '指你在本服務內建立的任何文字、繪圖、音訊、影片、分數、筆記或其他紀錄。',
      ],
    ],
    s2Title: '二、資格與守法',
    s2Body:
      '你聲明並保證：（甲）你具備使用本服務的法律能力；（乙）你將遵守所有適用法律；（丙）你不會以任何違法或被禁止的方式使用本服務。',
    s3Title: '三、本服務的性質',
    s3Body1:
      'Vessel 是一個個人心情紀錄 iOS 應用程式，旨在支援使用者的自我反思與個人成長。你可以在應用程式內紀錄簡短的心情分數、撰寫筆記，並建立私人紀錄（包括文字、塗鴉、聲音或短片）。',
    s3Body2Bold:
      'Vessel 並非醫療裝置，亦不能取代任何專業醫療、心理或精神科建議、診斷或治療。',
    s3Body2: '詳情請參閱第十條（精神健康聲明）。',
    s4Title: '四、本機儲存；無帳戶；備份功能',
    s4Bullets: [
      [
        '本機儲存：',
        '你在 Vessel 建立的所有內容都會儲存於你裝置上，置於 iOS 標準應用程式沙盒內。我們不為本服務營運任何伺服器、雲端同步或備份服務。',
      ],
      ['無帳戶：', '本服務無需註冊、登入或建立帳戶。'],
      [
        '備份功能：',
        '本服務內建「備份」按鈕。啟用後，本服務會將你的內容以 JSON 格式寫入你裝置上的本機儲存空間。該備份完全在本機建立及儲存；我們並沒有營運任何伺服器端備份、雲端同步或資料恢復機制。',
      ],
      [
        '不保證資料不遺失：',
        '備份功能僅為前端的便利功能，並不保證資料不會遺失。你須自行妥善保管備份檔（例如自行複製到你的 iCloud Drive、電腦或其他安全位置）。任何因裝置遺失、重設、回復原廠、解除安裝、檔案損毀或任何其他原因導致的資料遺失，我們概不負責。',
      ],
    ],
    s5Title: '五、你的內容',
    s5Body:
      '你在 Vessel 建立的所有內容皆屬於你。我們不會以任何目的存取、檢視、複製、傳輸或使用你的內容。由於我們從不接收你的內容，因此我們對其不持有任何權利，亦不就你所建立的任何紀錄之內容承擔責任。',
    s6Title: '六、合理使用',
    s6Body: '你同意不會：',
    s6List: [
      '以任何違反適用法律或法規的方式使用本服務；',
      '對本服務進行反向工程、反編譯或干擾，但法律明確允許者除外；',
      '試圖規避或破壞所安裝本服務的裝置之安全機制；或',
      '進行任何其他破壞或損害本服務完整性的行為。',
    ],
    s7Title: '七、知識產權',
    s7Body:
      'Vessel 軟件、名稱、設計、商標、標誌及相關資料均由我們或我們的授權人擁有，並受知識產權法保護。本條款僅授予你在獲授權之裝置上有限地使用本服務的權利，並不授予你任何其他權利。',
    s8Title: '八、按「現狀」提供；不提供任何保證',
    s8Body:
      '本服務按「現狀」及「可使用情況」提供。我們不就任何明示或默示之事項作出任何保證，包括適銷性、特定用途之適用性、準確性、完整性、時效性、可用性或不侵權。我們不保證本服務不會中斷、無錯誤或完全安全。你須自行承擔使用本服務的所有風險。',
    s9Title: '九、責任限制',
    s9Body:
      '在法律允許的最大範圍內，對於因你使用或無法使用本服務所引起或相關之任何間接、附帶、特別、衍生性或懲罰性損害，或任何資料、利潤或商譽之損失，我們概不負責。',
    s10Title: '十、精神健康聲明',
    s10Body1:
      'Vessel 僅供個人反思與自我認識之用。本服務提供的心情評分、日曆、圖表及日記功能並非診斷工具，亦不構成任何醫療、心理或精神科建議。紀錄心情並不代表你有任何精神健康問題；每個人都會有正常的情緒高低起伏。',
    s10Body2:
      '如你正經歷情緒困擾、精神健康危機或有自我傷害的念頭，請務必聯絡持牌的精神健康專業人士、當地緊急服務或認可的危機支援熱線。請勿以本服務取代專業協助。',
    s11Title: '十一、維護與變更',
    s11Body:
      '我們可隨時因技術、營運或法律原因更新、修改或停止本服務的任何部分。我們將盡商業上合理之努力減低影響，惟不保證會事前通知，亦不保證服務不中斷。',
    s12Title: '十二、私隱',
    s12Body:
      '請參閱我們的私隱政策，了解我們如何處理（或更準確地說，不處理）個人資料。你使用本服務亦受我們的私隱政策約束。',
    s13Title: '十三、終止',
    s13Body:
      '你可隨時透過從裝置上解除安裝本服務以停止使用。對於任何我們認為違反本條款，或對其他用戶、我們或第三方造成傷害的行為，我們亦可在不另行通知的情況下隨時終止或暫停其本服務存取權限。',
    s14Title: '十四、本條款之修改',
    s14Body:
      '我們可不時更新本條款。除非另有說明，更新後的條款於本服務內或我們的官方網頁張貼後即時生效。如你在更新後繼續使用本服務，即視為接受該等修改。',
    footer: '本文件自 2026 年 5 月 4 日起生效。',
  },
};

export default function VesselTerms() {
  const [lang, setLang] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    return localStorage.getItem('vesselDocLang') === 'zh' ? 'zh' : 'en';
  });
  const t = content[lang];

  const toggleLang = () => {
    const next = lang === 'en' ? 'zh' : 'en';
    setLang(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem('vesselDocLang', next);
    }
  };

  const styles = {
    page: {
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      fontFamily: 'Times, "Times New Roman", serif',
      color: '#000000',
      margin: 0,
      padding: 0,
    },
    container: {
      maxWidth: '8.5in',
      margin: '0 auto',
      padding: '1in',
      lineHeight: '1.5',
      fontSize: '12pt',
    },
    langToggle: {
      position: 'fixed',
      top: '20px',
      right: '24px',
      background: '#ffffff',
      border: '1.5px solid #000',
      padding: '6px 14px',
      fontFamily: 'Times, "Times New Roman", serif',
      fontSize: '11pt',
      cursor: 'pointer',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      fontWeight: 'bold',
      color: '#000',
      zIndex: 100,
      boxShadow: '2px 2px 0 #000',
    },
    header: {
      textAlign: 'center',
      marginBottom: '30px',
      borderBottom: '2px solid #000',
      paddingBottom: '20px',
    },
    title: {
      fontSize: '18pt',
      fontWeight: 'bold',
      margin: '0 0 15px 0',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    effectiveDate: {
      fontSize: '12pt',
      fontWeight: 'bold',
      margin: '0 0 10px 0',
    },
    serviceName: {
      fontSize: '12pt',
      fontWeight: 'bold',
      margin: '0 0 15px 0',
    },
    introText: {
      fontSize: '12pt',
      fontStyle: 'italic',
      margin: '0',
      textAlign: 'justify',
    },
    disclaimer: {
      margin: '0 0 30px 0',
      padding: '18px 22px',
      border: '1.5px solid #000',
      backgroundColor: '#fafafa',
    },
    disclaimerText: {
      margin: 0,
      fontSize: '11.5pt',
      lineHeight: '1.6',
      textAlign: 'justify',
      fontWeight: 'bold',
    },
    section: {
      marginBottom: '25px',
      pageBreakInside: 'avoid',
    },
    sectionNumber: {
      fontSize: '14pt',
      fontWeight: 'bold',
      marginBottom: '10px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    paragraph: {
      fontSize: '12pt',
      marginBottom: '12px',
      textAlign: 'justify',
      textIndent: lang === 'zh' ? '0' : '0.5in',
      lineHeight: '1.5',
    },
    paragraphNoIndent: {
      fontSize: '12pt',
      marginBottom: '12px',
      textAlign: 'justify',
      lineHeight: '1.5',
    },
    list: {
      paddingLeft: '0.75in',
      marginBottom: '12px',
    },
    listItem: {
      fontSize: '12pt',
      marginBottom: '6px',
      listStyleType: 'disc',
    },
    definitionList: {
      paddingLeft: '0.5in',
      marginBottom: '12px',
    },
    definitionItem: {
      fontSize: '12pt',
      marginBottom: '10px',
      listStyleType: 'none',
    },
    definitionTerm: {
      fontWeight: 'bold',
      display: 'inline',
    },
    footer: {
      marginTop: '40px',
      paddingTop: '20px',
      borderTop: '1px solid #000',
      textAlign: 'center',
      fontSize: '10pt',
      color: '#666',
    },
  };

  return (
    <div style={styles.page} lang={lang === 'zh' ? 'zh-Hant' : 'en'}>
      <button
        type="button"
        onClick={toggleLang}
        style={styles.langToggle}
        aria-label={lang === 'en' ? 'Switch to Chinese' : 'Switch to English'}
      >
        {t.toggleLabel}
      </button>

      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>{t.title}</h1>
          <p style={styles.effectiveDate}>{t.effectiveDate}</p>
          <p style={styles.serviceName}>{t.serviceName}</p>
          <p style={styles.introText}>{t.introText}</p>
        </div>

        <div style={styles.disclaimer}>
          <p style={styles.disclaimerText}>{t.disclaimer}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s1Title}</h2>
          <ul style={styles.definitionList}>
            {t.defs.map(([term, body], i) => (
              <li key={i} style={styles.definitionItem}>
                <span style={styles.definitionTerm}>{term}</span> {body}
              </li>
            ))}
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s2Title}</h2>
          <p style={styles.paragraph}>{t.s2Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s3Title}</h2>
          <p style={styles.paragraphNoIndent}>{t.s3Body1}</p>
          <p style={styles.paragraphNoIndent}>
            <strong>{t.s3Body2Bold}</strong>
            {t.s3Body2}
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s4Title}</h2>
          {t.s4Bullets.map(([term, body], i) => (
            <p key={i} style={styles.paragraphNoIndent}>
              <strong>{term}</strong>
              {body}
            </p>
          ))}
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s5Title}</h2>
          <p style={styles.paragraph}>{t.s5Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s6Title}</h2>
          <p style={styles.paragraphNoIndent}>{t.s6Body}</p>
          <ul style={styles.list}>
            {t.s6List.map((item, i) => (
              <li key={i} style={styles.listItem}>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s7Title}</h2>
          <p style={styles.paragraphNoIndent}>{t.s7Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s8Title}</h2>
          <p style={styles.paragraph}>{t.s8Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s9Title}</h2>
          <p style={styles.paragraph}>{t.s9Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s10Title}</h2>
          <p style={styles.paragraphNoIndent}>{t.s10Body1}</p>
          <p style={styles.paragraphNoIndent}>{t.s10Body2}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s11Title}</h2>
          <p style={styles.paragraph}>{t.s11Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s12Title}</h2>
          <p style={styles.paragraph}>{t.s12Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s13Title}</h2>
          <p style={styles.paragraph}>{t.s13Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s14Title}</h2>
          <p style={styles.paragraph}>{t.s14Body}</p>
        </div>

        <div style={styles.footer}>
          <p>{t.footer}</p>
        </div>
      </div>
    </div>
  );
}
